import type { TranslationKey } from "@/i18n";

export type CategoryKey =
  | "people"
  | "nature"
  | "coastal"
  | "sdg"
  | "tech"
  | "health";

// All categories now use shades of the single blue ramp - distinguishable but monochrome.
export const CATEGORIES: { key: CategoryKey; labelKey: TranslationKey; token: string }[] = [
  { key: "people",  labelKey: "stories.category.people",  token: "var(--blue-800)" },
  { key: "nature",  labelKey: "stories.category.nature",  token: "var(--blue-600)" },
  { key: "coastal", labelKey: "stories.category.coastal", token: "var(--blue-500)" },
  { key: "sdg",     labelKey: "stories.category.sdg",     token: "var(--blue-700)" },
  { key: "tech",    labelKey: "stories.category.tech",    token: "var(--blue-400)" },
  { key: "health",  labelKey: "stories.category.health",  token: "var(--blue-300)" },
];

export function categoryColor(k: CategoryKey) {
  return CATEGORIES.find((c) => c.key === k)?.token ?? "var(--blue-600)";
}

/**
 * Accessible foreground text color per category badge.
 * Light backgrounds (health, tech, coastal, nature) pair with deep obsidian navy #00111A
 * to achieve 4.75:1 to 9.77:1 contrast ratios, surpassing WCAG AA 4.5:1 requirements.
 * Dark backgrounds (people, sdg) pair with white #FFFFFF (5.22:1 to 6.83:1).
 */
export function categoryTextColor(k: CategoryKey): string {
  switch (k) {
    case "health":
    case "tech":
    case "coastal":
    case "nature":
      return "#00111A";
    default:
      return "#FFFFFF";
  }
}

export function categoryLabelKey(k: CategoryKey): TranslationKey {
  return CATEGORIES.find((c) => c.key === k)?.labelKey ?? "stories.category.people";
}

/** On-brand hero gradient per category - used whenever a story has no cover photo. */
const CATEGORY_GRADIENTS: Record<CategoryKey, string> = {
  people: "linear-gradient(135deg,#0088B7 0%,#004A63 100%)",
  nature: "linear-gradient(135deg,#00A1DB 0%,#006283 100%)",
  coastal: "linear-gradient(135deg,#00AFEF 0%,#00759C 100%)",
  sdg: "linear-gradient(135deg,#00759C 0%,#00243A 100%)",
  tech: "linear-gradient(135deg,#00AFEF 0%,#004A63 100%)",
  health: "linear-gradient(135deg,#0088B7 0%,#00394D 100%)",
};

export function categoryGradient(k: CategoryKey) {
  return CATEGORY_GRADIENTS[k] ?? CATEGORY_GRADIENTS.people;
}

/** Build a list of categories that actually have stories, ordered by story count and capped at 5. */
export function categoriesFromStories(stories: { category?: string }[]): CategoryKey[] {
  const counts = new Map<string, number>();
  for (const s of stories) {
    if (!s.category) continue;
    counts.set(s.category, (counts.get(s.category) ?? 0) + 1);
  }
  return (CATEGORIES as { key: CategoryKey; labelKey: TranslationKey; token: string }[])
    .filter((c) => (counts.get(c.key) ?? 0) > 0)
    .sort((a, b) => (counts.get(b.key) ?? 0) - (counts.get(a.key) ?? 0))
    .slice(0, 5)
    .map((c) => c.key);
}
